import React, { useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  IconButton,
} from "@mui/material";

import { Logout as LogoutIcon } from "@mui/icons-material";

import { Settings as SettingsIcon } from "@mui/icons-material";

import { ThemeContext } from "../../context/ThemeContext";

import { ReactComponent as DarkIcon } from "../../assets/viewmode/dark.svg";
import { ReactComponent as LightIcon } from "../../assets/viewmode/light.svg";

import { ViewModeSelector } from "./ViewModeSelector";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { useSnackbar } from "notistack";
import { SectionSelectbar } from "./SectionSelectbar";
import useMediaQuery from "@mui/material/useMediaQuery";
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';

export const ModeChangebar = ({
  visibleViewMode = true,
  isDisable = false,
}) => {
  const baseUrl = window.location.origin;
  const { avatar } = useAuthContext();
  const avatarURL = useMemo(() => {
    if (!avatar) return undefined;
    try {
      new URL(avatar);
      return avatar;
    } catch {
      return `${baseUrl}/avatars/${avatar}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatar]);
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery("(max-width:425px)"); // Check if screen width is less than 768px
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { darkMode, toggleDarkMode } = useContext(ThemeContext);
  const { signOut } = useAuthContext();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut();
    enqueueSnackbar("Logout Successful!", { variant: "success" });
  };

  const handleReferralClick = () => {
    navigate("/refer");
  };

  const handleProfileSettings = () => {
    handleClose();
    navigate("/profile");
  };

  return (
    <Box display="flex" alignItems="center">
      <Box display={{ xs: "block", sm: "none" }} sx={{ mr: isMobile ? 1 : 4 }}>
        <SectionSelectbar />
      </Box>
      {visibleViewMode && (
        <Box sx={{ mr: isMobile ? 1 : 3 }}>
          <ViewModeSelector isDisable={isDisable} />
        </Box>
      )}
      <IconButton
        sx={{
          mr: isMobile ? 1 : 2,
          fontSize: "40px",
          mx: 2,
          bgcolor: "rgba(59,92,251,0.08)", // lighter, less saturated
          color: "#3B5CFB",
          borderRadius: "100px",
          "&:hover": {
            bgcolor: "rgba(59,92,251,0.16)",
          },
          boxShadow: "none", // remove shadow
        }}
        onClick={handleReferralClick}
      >
        <CardGiftcardIcon style={{ fontSize: 40 }} />
      </IconButton>
      <IconButton sx={{ mr: isMobile ? 1 : 2 }} onClick={toggleDarkMode}>
        {darkMode ? <LightIcon /> : <DarkIcon />}
      </IconButton>
      <Avatar
        alt="User"
        src={avatarURL}
        sx={{
          cursor: "pointer",
          width: isMobile ? 50 : 60,
          height: isMobile ? 50 : 60,
        }}
        onClick={handleClick}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleProfileSettings}>
          <ListItemIcon>
            <SettingsIcon width={24} height={24} />
          </ListItemIcon>
          <Typography>Profile</Typography>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon width={24} height={24} />
          </ListItemIcon>
          <Typography>Logout</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

ModeChangebar.propTypes = {
  visibleViewMode: PropTypes.bool,
  isDisable: PropTypes.bool,
};
